import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  WorkspaceFilterBarSkeleton,
  WorkspaceTabsSkeleton,
} from '@/components/reusable/WorkspaceSkeleton';

/**
 * The single loading state for the Automations workspace.
 *
 * Opening Automations used to flash three different loaders in sequence before
 * the flows appeared:
 *   1. a full-page "Loading..." — the `FeatureRoute` / `RoleRoute` gates waiting
 *      on entitlements and `/onboarding/qa`
 *   2. a bare spinner — the route-level `<Suspense>` fallback in `App.tsx`
 *   3. a "Loading automation flows..." spinner card — the list fetching flows
 *
 * All three now render this skeleton, so the layout appears once and stays put
 * until the real workspace replaces it. The measurements mirror
 * `AutomationsWorkspaceShell` (tabs), `AutomationsWorkspaceFilterBar` (toolbar)
 * and the flow card grid rendered by `Flows`, `InstagramFlowsList` and
 * `FacebookFlowsList`, so nothing shifts when the data lands.
 */

/** One flow card — mirrors the `rounded-2xl` card used by all three networks. */
export const AutomationFlowCardSkeleton: React.FC = () => (
  <div className="flex flex-col gap-3 rounded-2xl border border-[#EAECF0] bg-white p-4">
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <Skeleton className="h-[15px] w-[60%] rounded" />
        <Skeleton className="mt-1.5 h-[11px] w-[40%] rounded" />
      </div>
      <Skeleton className="h-[21px] w-16 shrink-0 rounded-md" />
      <Skeleton className="h-5 w-5 shrink-0 rounded" />
    </div>

    <div className="flex flex-wrap items-center gap-1.5">
      <Skeleton className="h-[26px] w-24 rounded-md" />
      <Skeleton className="h-[26px] w-16 rounded-md" />
      <Skeleton className="h-[26px] w-14 rounded-md" />
    </div>

    <div className="grid grid-cols-4 gap-2 border-t border-[#F2F4F7] pt-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <Skeleton className="h-[10px] w-10 rounded" />
          <Skeleton className="mt-1.5 h-[13px] w-8 rounded" />
        </div>
      ))}
    </div>
  </div>
);

/** The flow card grid on its own — used while a list refetches in place. */
export const AutomationFlowsGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div
    className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
    aria-busy="true"
    aria-label="Loading automation flows"
    data-testid="automation-flows-skeleton"
  >
    {Array.from({ length: count }).map((_, i) => (
      <AutomationFlowCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * The whole workspace: tabs, toolbar and flow grid. Used by the route guards
 * and the `<Suspense>` fallback so the page never paints an empty screen.
 * Three tabs (WhatsApp / Instagram / Facebook), Status + Trigger filters, then
 * Template Library and Create automation.
 */
export const AutomationsPageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-h-full min-w-0 flex-col bg-white"
    aria-busy="true"
    aria-label="Loading automations"
    data-testid="automations-page-skeleton"
  >
    <WorkspaceTabsSkeleton widths={[64, 76, 72]} />
    <WorkspaceFilterBarSkeleton filters={2} actions={[148, 168]} />
    <div className="min-h-0 min-w-0 flex-1 overflow-hidden px-[15px] pb-4">
      <AutomationFlowsGridSkeleton />
    </div>
  </div>
);

export default AutomationsPageSkeleton;
